import { Suspense, useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation, useMatch } from 'react-router-dom'
import GameMenuSidebar from '@components/main/GameMenuSidebar'
import MainHeader from '@components/main/MainHeader'
import PageFallback from '@components/main/PageFallback'
import { pageTitle } from '@routing/pageTitle'
import { useAuthStore } from '@stores/authStore'

interface GameHostPageProps {
  subdomain: string
}

function GameHostPage({ subdomain }: GameHostPageProps) {
  const location = useLocation()
  const session = useAuthStore((state) => state.session)
  const isLoading = useAuthStore((state) => state.isLoading)
  const loadSession = useAuthStore((state) => state.loadSession)
  const currentPageTitle = pageTitle(location.pathname)
  const currentAppTitle = session?.currentApp?.name || subdomain
  const isUsersPage = Boolean(useMatch('/users/*'))
  const isAdPage = Boolean(useMatch('/ad/*'))
  const isReportsPage = Boolean(useMatch('/reports/*'))
  const hideSidebar = isUsersPage || isAdPage || isReportsPage
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)

  useEffect(() => {
    void loadSession()
  }, [loadSession])

  useEffect(() => {
    document.title = `GRStat | ${currentAppTitle} | ${currentPageTitle}`
  }, [currentAppTitle, currentPageTitle])

  if (session && !session.authenticated) {
    const next = `${location.pathname}${location.search}${location.hash}`
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  }

  return (
    <main className="min-h-svh bg-slate-50 text-slate-950 dark:bg-zinc-950 dark:text-slate-100">
      <MainHeader />

      <div
        className={`grid h-[calc(100svh-4rem)] min-h-0 max-[820px]:h-auto max-[820px]:grid-cols-1 ${
          hideSidebar
            ? 'grid-cols-1'
            : isSidebarCollapsed
              ? 'grid-cols-[48px_1fr]'
              : 'grid-cols-[240px_1fr]'
        }`}
      >
        {hideSidebar ? null : (
          <GameMenuSidebar
            isCollapsed={isSidebarCollapsed}
            onToggleCollapsed={() => setIsSidebarCollapsed((value) => !value)}
          />
        )}

        <section className="min-w-0 overflow-y-auto p-6 max-[820px]:overflow-visible max-[820px]:p-4">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="m-0 text-2xl font-semibold tracking-normal">
                {currentPageTitle}
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {currentAppTitle}
              </p>
            </div>
          </div>

          {session?.authenticated ? (
            <Suspense fallback={<PageFallback />}>
              <Outlet />
            </Suspense>
          ) : isLoading ? (
            <PageFallback />
          ) : null}
        </section>
      </div>
    </main>
  )
}

export default GameHostPage
